import React, { useState } from "react";
import { KeyRound, Copy, ShieldAlert, Ban } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const inp = "mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#A3E635]/60";
const btn = "bg-[#A3E635] text-black font-bold rounded-xl px-4 py-2 text-sm disabled:opacity-50";
const errText = (t, e) => t(e?.response?.data?.detail || "Aktion fehlgeschlagen.");

function StepUpFields({ pw, setPw, code, setCode, prefix }) {
  const { t } = useI18n();
  return (
    <>
      <label className="block text-xs text-gray-400 mt-3">{t("Ihr Passwort")}
        <input type="password" autoComplete="current-password" value={pw} onChange={e => setPw(e.target.value)}
          className={inp} data-testid={`${prefix}-password`} required /></label>
      <label className="block text-xs text-gray-400 mt-3">{t("Zwei-Faktor-Code")}
        <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} required
          onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className={`${inp} tracking-widest`} data-testid={`${prefix}-code`} /></label>
    </>
  );
}

function TokenOnce({ token, onClose }) {
  const { t } = useI18n();
  const copy = () => navigator.clipboard?.writeText(token)
    .then(() => toast.success(t("Token kopiert")))
    .catch(() => toast.error(t("Kopieren nicht möglich – bitte manuell markieren")));
  return (
    <div data-testid="pc-pair-token-panel">
      <p className="text-sm text-amber-300 flex gap-2"><ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
        {t("Das Token wird nur jetzt einmal angezeigt und nirgends gespeichert. Kopieren Sie es direkt in die Token-Datei auf dem PC (Schritt 3 der Anleitung).")}</p>
      <code className="block mt-3 p-3 rounded-xl bg-black/50 border border-white/10 text-xs break-all select-all font-mono" data-testid="pc-pair-token">{token}</code>
      <div className="flex flex-wrap gap-2 mt-4">
        <button type="button" onClick={copy} className="border border-white/15 rounded-xl px-3 py-2 text-sm inline-flex items-center gap-1.5" data-testid="pc-pair-token-copy"><Copy className="w-4 h-4" /> {t("Kopieren")}</button>
        <button type="button" onClick={onClose} className={btn} data-testid="pc-pair-token-done">{t("Gespeichert – schliessen")}</button>
      </div>
    </div>
  );
}

export function PairDialog({ open, onClose, onDone }) {
  const { t } = useI18n();
  const [label, setLabel] = useState("");
  const [env, setEnv] = useState("production");
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(null);
  const [err, setErr] = useState(null);
  const close = () => { setToken(null); setPw(""); setCode(""); setLabel(""); setErr(null); onClose(); };
  const submit = e => {
    e.preventDefault(); setBusy(true); setErr(null);
    api.post("/admin/pc-relay/runners", { label, environment: env, password: pw, code })
      .then(r => { setToken(r.data.token); onDone(); })
      .catch(e2 => setErr(errText(t, e2)))
      .finally(() => { setBusy(false); setPw(""); setCode(""); });
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && !busy && close()}>
      <DialogContent className="bg-[#0E0F12] border-white/10 text-white max-w-lg" data-testid="pc-pair-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#A3E635]" /> {t("Gerät koppeln")}</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">{t("Erzeugt ein Geräte-Token, das nur Aufträge der PC-Produktion abholen und Status melden darf: keine Anmeldung in der App, keine Kundendaten, keine Shell- oder Dateiaktionen. Gültig 90 Tage, jederzeit sperrbar.")}</DialogDescription>
        </DialogHeader>
        {token ? <TokenOnce token={token} onClose={close} /> : (
          <form onSubmit={submit}>
            <label className="block text-xs text-gray-400">{t("Gerätename")}
              <input value={label} onChange={e => setLabel(e.target.value)} maxLength={60} required className={inp} data-testid="pc-pair-label" placeholder={t("z. B. Martins PC")} /></label>
            <fieldset className="mt-3 text-sm" data-testid="pc-pair-env">
              <legend className="text-xs text-gray-400">{t("Umgebung")}</legend>
              {[["production", "Produktion (echte Aufträge)"], ["test", "TEST (nur Verbindungstest)"]].map(([v, l]) => (
                <label key={v} className="flex items-center gap-2 mt-1"><input type="radio" name="pc-env" checked={env === v} onChange={() => setEnv(v)} data-testid={`pc-pair-env-${v}`} /> {t(l)}</label>))}
            </fieldset>
            <StepUpFields pw={pw} setPw={setPw} code={code} setCode={setCode} prefix="pc-pair" />
            {err && <p className="text-xs text-red-300 mt-3" data-testid="pc-pair-error">{err}</p>}
            <button type="submit" disabled={busy} className={`${btn} mt-4`} data-testid="pc-pair-submit">{busy ? t("Prüfe…") : t("Identität bestätigen und koppeln")}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RevokeDialog({ runner, onClose, onDone }) {
  const { t } = useI18n();
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = e => {
    e.preventDefault(); setBusy(true); setErr(null);
    api.post(`/admin/pc-relay/runners/${runner.runner_id}/revoke`, { password: pw, code })
      .then(() => { toast.success(t("Gerät gesperrt")); onDone(); onClose(); })
      .catch(e2 => setErr(errText(t, e2)))
      .finally(() => { setBusy(false); setPw(""); setCode(""); });
  };
  return (
    <Dialog open={!!runner} onOpenChange={o => !o && !busy && onClose()}>
      <DialogContent className="bg-[#0E0F12] border-white/10 text-white max-w-lg" data-testid="pc-revoke-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-red-300" /> {t("Gerät sperren")}: {runner?.label}</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">{t("Das Token wird sofort ungültig. Laufende Aufträge dieses Geräts werden als fehlgeschlagen markiert und nicht automatisch an ein anderes Gerät gegeben.")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit}>
          <StepUpFields pw={pw} setPw={setPw} code={code} setCode={setCode} prefix="pc-revoke" />
          {err && <p className="text-xs text-red-300 mt-3" data-testid="pc-revoke-error">{err}</p>}
          <button type="submit" disabled={busy} className="mt-4 bg-red-400 text-black font-bold rounded-xl px-4 py-2 text-sm disabled:opacity-50" data-testid="pc-revoke-submit">{busy ? t("Prüfe…") : t("Identität bestätigen und sperren")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
